import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../config';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'motion/react';
import { toast } from 'sonner';
import LandingHeader from '../components/LandingHeader';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

import {
  ArrowsClockwise,
  CheckCircle,
  CircleNotch,
  Envelope,
  WarningCircle
} from '@phosphor-icons/react';
const VerifyEmail = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState('pending'); // 'pending', 'success', 'error'
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const emailParam = searchParams.get('email');
    const tokenParam = searchParams.get('token');
    
    if (emailParam) {
      setEmail(emailParam);
    }

    if (tokenParam) {
      setToken(tokenParam);
      verifyEmail(tokenParam);
    }
  }, [searchParams]);

  const verifyEmail = async (verificationToken) => {
    setIsVerifying(true);
    setVerificationStatus('pending');

    try {
      const response = await fetch(
        `${BACKEND_URL}/api/auth/verify-email?token=${verificationToken}`,
        {
          method: 'GET'
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Verification failed');
      }

      setVerificationStatus('success');
      toast.success('Email verified successfully!');
      
      // Check if user has a pending blueprint to claim
      const pendingBlueprint = localStorage.getItem('pending_blueprint_claim');
      const postAuthRedirect = localStorage.getItem('post_auth_redirect');
      
      // Redirect to login (which will then redirect to blueprint/continue if pending)
      setTimeout(() => {
        if (pendingBlueprint && postAuthRedirect) {
          // Preserve the redirect intent in the login URL
          navigate('/login?redirect=' + encodeURIComponent(postAuthRedirect));
        } else {
          navigate('/login');
        }
      }, 2000);

    } catch (error) {
      setVerificationStatus('error');
      setErrorMessage(error.message);
      toast.error(error.message);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendVerification = async () => {
    if (!email) {
      toast.error('Email address not found');
      return;
    }

    setIsResending(true);

    try {
      const response = await fetch(
        `${BACKEND_URL}/api/auth/resend-verification`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ email })
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to resend verification email');
      }

      toast.success('Verification email sent! Check your inbox.');

    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#161B26]">
      <SEO 
        title="Verify Email - Man & Machine AI Opportunity Analyzer"
        description="Verify your email address to complete registration"
      />
      <LandingHeader />

      <div className="pt-32 pb-20">
        <div className="max-w-md mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass rounded-3xl p-8"
          >
            {/* Verification Status */}
            {verificationStatus === 'pending' && !token && (
              <>
                <div className="text-center mb-8">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#6366F1]/20 to-[#EC4899]/20 flex items-center justify-center mx-auto mb-4">
                    <Envelope size={32} className="text-[#6366F1]" />
                  </div>
                  <h1 className="text-3xl font-bold text-white mb-2">
                    Check Your Email
                  </h1>
                  <p className="text-[#A0AEC0]">
                    We've sent a verification link to
                  </p>
                  <p className="text-white font-semibold mt-2">
                    {email || 'your email address'}
                  </p>
                </div>

                <div className="space-y-4 mb-8">
                  <div className="flex items-start gap-3 text-[#A0AEC0] text-sm">
                    <CheckCircle size={20} className="text-[#14F195] flex-shrink-0 mt-0.5" />
                    <span>Click the verification link in your email</span>
                  </div>
                  <div className="flex items-start gap-3 text-[#A0AEC0] text-sm">
                    <CheckCircle size={20} className="text-[#14F195] flex-shrink-0 mt-0.5" />
                    <span>The link will expire in 24 hours</span>
                  </div>
                  <div className="flex items-start gap-3 text-[#A0AEC0] text-sm">
                    <CheckCircle size={20} className="text-[#14F195] flex-shrink-0 mt-0.5" />
                    <span>Check your spam folder if you don't see it</span>
                  </div>
                </div>

                <button
                  onClick={handleResendVerification}
                  disabled={isResending}
                  className="w-full py-3 rounded-full glass hover:bg-[rgba(255,255,255,0.05)] text-white font-semibold transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isResending ? (
                    <>
                      <CircleNotch size={20} className="animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <ArrowsClockwise size={20} />
                      Resend Verification Email
                    </>
                  )}
                </button>
              </>
            )}

            {verificationStatus === 'pending' && token && isVerifying && (
              <div className="text-center">
                <CircleNotch size={64} className="text-[#6366F1] animate-spin mx-auto mb-6" />
                <h2 className="text-2xl font-bold text-white mb-2">Verifying...</h2>
                <p className="text-[#A0AEC0]">Please wait while we verify your email</p>
              </div>
            )}

            {verificationStatus === 'success' && (
              <>
                <div className="text-center mb-8">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#14F195]/20 to-[#6366F1]/20 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle size={32} className="text-[#14F195]" />
                  </div>
                  <h1 className="text-3xl font-bold text-white mb-2">
                    Email Verified!
                  </h1>
                  <p className="text-[#A0AEC0]">
                    Your email has been successfully verified
                  </p>
                </div>

                <div className="text-center">
                  <p className="text-[#A0AEC0] mb-6">
                    Redirecting to login page...
                  </p>
                  <button
                    onClick={() => navigate('/login')}
                    className="relative overflow-hidden rounded-full group inline-flex"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-[#6366F1] via-[#8B5CF6] to-[#EC4899]"></div>
                    <span className="relative z-10 flex items-center justify-center gap-2 px-8 py-3 text-white font-bold">
                      Go to Login
                    </span>
                  </button>
                </div>
              </>
            )}

            {verificationStatus === 'error' && (
              <>
                <div className="text-center mb-8">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#EF4444]/20 to-[#EC4899]/20 flex items-center justify-center mx-auto mb-4">
                    <WarningCircle size={32} className="text-[#EF4444]" />
                  </div>
                  <h1 className="text-3xl font-bold text-white mb-2">
                    Verification Failed
                  </h1>
                  <p className="text-[#EF4444]">
                    {errorMessage || 'The verification link is invalid or expired'}
                  </p>
                </div>

                <div className="space-y-3">
                  <button
                    onClick={handleResendVerification}
                    disabled={isResending || !email}
                    className="w-full relative overflow-hidden rounded-full group disabled:opacity-50"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-[#6366F1] via-[#8B5CF6] to-[#EC4899]"></div>
                    <span className="relative z-10 flex items-center justify-center gap-2 py-3 text-white font-bold">
                      {isResending ? (
                        <>
                          <CircleNotch size={20} className="animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <ArrowsClockwise size={20} />
                          Request New Link
                        </>
                      )}
                    </span>
                  </button>

                  <button
                    onClick={() => navigate('/register')}
                    className="w-full py-3 rounded-full glass hover:bg-[rgba(255,255,255,0.05)] text-white font-semibold transition-all"
                  >
                    Back to Registration
                  </button>
                </div>
              </>
            )}
          </motion.div>

          {/* Back to Home */}
          <div className="text-center mt-6">
            <button
              onClick={() => navigate('/')}
              className="text-[#A0AEC0] hover:text-white transition-colors text-sm"
            >
              ← Back to Home
            </button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default VerifyEmail;
